import { Routes, Route } from "react-router-dom";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import DashboardLayout from "../../components/DashboardLayout";
import StatusBadge from "../../components/StatusBadge";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";

const NAV = [
  { to: "/espace-centre", label: "Nouvelles demandes", end: true },
  { to: "/espace-centre/en-cours", label: "Demandes en cours" },
  { to: "/espace-centre/rendez-vous", label: "Rendez-vous" },
  { to: "/espace-centre/historique", label: "Historique" },
  { to: "/espace-centre/profil", label: "Profil du centre" },
];

function useAppts(status) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const refresh = () => {
    setLoading(true);
    const params = status ? { status } : {};
    api.get("/center/appointments", { params }).then((r) => setItems(r.data.appointments || [])).finally(() => setLoading(false));
  };
  useEffect(() => { refresh(); }, [status]);
  return { items, loading, refresh };
}

function ApptCard({ a, onUpdate }) {
  const [comment, setComment] = useState("");
  const [altDate, setAltDate] = useState("");
  const [target, setTarget] = useState("");

  const act = async () => {
    if (!target) return toast.error("Sélectionnez une action");
    try {
      await api.post(`/appointments/${a.id}/status`, {
        status: target, comment,
        alternative_date: target === "ALTERNATIVE_DATE_PROPOSED" ? altDate : null,
      });
      toast.success("Statut mis à jour");
      onUpdate();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const note = async () => {
    if (!comment.trim()) return toast.error("Note vide");
    try {
      await api.post(`/appointments/${a.id}/notes`, { text: comment });
      toast.success("Note ajoutée");
      setComment("");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const options = [
    { v: "CENTER_AVAILABLE", t: "Disponible" },
    { v: "ALTERNATIVE_DATE_PROPOSED", t: "Proposer une autre date" },
    { v: "CENTER_UNAVAILABLE", t: "Indisponible" },
    ...(a.status === "CONFIRMED" ? [{ v: "COMPLETED", t: "Marquer réalisé" }, { v: "NO_SHOW", t: "Absence" }] : []),
  ];

  return (
    <div className="bg-white p-6 rounded-2xl border border-slate-200 card-lux" data-testid={`center-appt-${a.id}`}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-sm text-slate-500">Réf. {a.reference}</div>
          <div className="mt-1 font-heading text-xl text-primary-blue">
            {a.client.first_name} {a.client.last_name}
          </div>
          <div className="text-sm text-slate-600">{a.client.email} · {a.client.phone}</div>
          <div className="mt-2 text-sm text-slate-700">
            {a.reason} · {a.preferred_date} ({a.time_preference === "morning" ? "matin" : "après-midi"})
          </div>
        </div>
        <StatusBadge status={a.status} />
      </div>

      <div className="mt-5 grid md:grid-cols-2 gap-4">
        <div>
          <Label className="text-sm">Action</Label>
          <Select value={target} onValueChange={setTarget}>
            <SelectTrigger className="mt-1 h-10" data-testid={`center-appt-action-${a.id}`}><SelectValue placeholder="Choisir…" /></SelectTrigger>
            <SelectContent>
              {options.map((o) => <SelectItem key={o.v} value={o.v}>{o.t}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {target === "ALTERNATIVE_DATE_PROPOSED" && (
          <div>
            <Label className="text-sm">Date alternative</Label>
            <Input type="date" value={altDate} onChange={(e) => setAltDate(e.target.value)} className="mt-1 h-10" />
          </div>
        )}
      </div>
      <div className="mt-4">
        <Label className="text-sm">Commentaire / note interne</Label>
        <Textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={3} className="mt-1" />
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        <Button onClick={act} className="bg-primary-blue text-white" data-testid={`center-appt-submit-${a.id}`}>Envoyer la réponse</Button>
        <Button onClick={note} variant="outline">Ajouter une note</Button>
      </div>
      <div className="mt-2 text-xs text-slate-500">La confirmation définitive est réalisée par un administrateur.</div>
    </div>
  );
}

function List({ statuses, title }) {
  const { items, loading, refresh } = useAppts(null);
  const filtered = statuses ? items.filter((a) => statuses.includes(a.status)) : items;
  return (
    <div>
      <h1 className="font-heading text-3xl text-primary-blue">{title}</h1>
      {loading && <div className="mt-4 text-slate-500">Chargement…</div>}
      {!loading && filtered.length === 0 && (
        <div className="mt-6 bg-white p-8 rounded-2xl border border-slate-200 text-slate-600">Aucun dossier.</div>
      )}
      <div className="mt-6 space-y-4">
        {filtered.map((a) => <ApptCard key={a.id} a={a} onUpdate={refresh} />)}
      </div>
    </div>
  );
}

function CenterProfile() {
  const { user } = useAuth();
  const [center, setCenter] = useState(null);
  useEffect(() => {
    if (user?.center_id) {
      api.get(`/centers/${user.center_id}`).then((r) => setCenter(r.data.center));
    }
  }, [user]);
  if (!user?.center_id) return <div>Aucun centre associé à ce compte.</div>;
  if (!center) return <div className="text-slate-500">Chargement…</div>;
  return (
    <div>
      <h1 className="font-heading text-3xl text-primary-blue">Profil du centre</h1>
      <div className="mt-6 bg-white p-6 rounded-2xl border border-slate-200 card-lux space-y-2">
        <div><strong>Nom :</strong> {center.name}</div>
        <div><strong>Réseau :</strong> {center.network_slug}</div>
        <div><strong>Adresse :</strong> {center.address}, {center.postal_code} {center.city}</div>
        {center.phone && <div><strong>Téléphone :</strong> {center.phone}</div>}
        {center.email && <div><strong>E-mail :</strong> {center.email}</div>}
        {center.hours && <div><strong>Horaires :</strong> {center.hours}</div>}
        <div className="text-sm text-slate-500 mt-4">Pour toute modification, contactez un administrateur.</div>
      </div>
    </div>
  );
}

export default function CenterDashboard() {
  return (
    <DashboardLayout title="Espace centre" subtitle="Centre partenaire" nav={NAV}>
      <Routes>
        <Route index element={<List statuses={["NEW_REQUEST", "ASSIGNED_TO_CENTER"]} title="Nouvelles demandes" />} />
        <Route path="en-cours" element={<List statuses={["WAITING_CENTER_RESPONSE", "CENTER_AVAILABLE", "ALTERNATIVE_DATE_PROPOSED", "CENTER_UNAVAILABLE", "PENDING_ADMIN_CONFIRMATION"]} title="Demandes en cours" />} />
        <Route path="rendez-vous" element={<List statuses={["CONFIRMED"]} title="Rendez-vous confirmés" />} />
        <Route path="historique" element={<List statuses={["COMPLETED", "NO_SHOW", "CANCELLED", "CLOSED"]} title="Historique" />} />
        <Route path="profil" element={<CenterProfile />} />
      </Routes>
    </DashboardLayout>
  );
}
